import React, { useState } from 'react';
import type { BundleScanSubsetComponent } from '../../schemas/apiResponse.schemas';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import ProductImage from '../shared/ProductImage';
import ProductInfoModal from '../shared/ProductInfoModal';

interface AllowlistProductInfoItem {
  readonly sku: string;
  readonly name?: string;
  readonly itemName?: string;
}

interface BundleAllowlistCardProps {
  readonly parentSku: string;
  readonly parentName?: string;
  readonly components: readonly BundleScanSubsetComponent[];
  readonly removeDisabled?: boolean;
  readonly removeLabel?: string;
  readonly onRemove?: () => void;
  readonly children?: React.ReactNode;
  readonly expandedFooter?: React.ReactNode;
  readonly emptyComponentsLabel?: string;
  readonly onRemoveComponent?: (sku: string) => void;
  readonly onQuantityChange?: (sku: string, quantity: number) => void;
  readonly componentRemoveDisabled?: boolean;
}

interface OpenProductButtonProps {
  readonly sku: string;
  readonly name?: string;
  readonly className: string;
  readonly ariaLabel?: string;
  readonly children: React.ReactNode;
  readonly onOpen: (sku: string, name?: string) => void;
}

function toProductInfoItem(sku: string, name?: string): AllowlistProductInfoItem {
  const trimmed = name?.trim();
  if (!trimmed) {
    return { sku };
  }
  return { sku, name: trimmed, itemName: trimmed };
}

function OpenProductButton({
  sku,
  name,
  className,
  ariaLabel,
  children,
  onOpen,
}: OpenProductButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      onClick={() => onOpen(sku, name)}
    >
      {children}
    </button>
  );
}

function BundleAllowlistCard({
  parentSku,
  parentName,
  components,
  removeDisabled,
  removeLabel,
  onRemove,
  children,
  expandedFooter,
  emptyComponentsLabel = 'No catalog components found',
  onRemoveComponent,
  onQuantityChange,
  componentRemoveDisabled = false,
}: BundleAllowlistCardProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [productInfoItem, setProductInfoItem] = useState<AllowlistProductInfoItem | null>(null);

  const openProductInfo = (sku: string, name?: string): void => {
    setProductInfoItem(toProductInfoItem(sku, name));
  };

  return (
    <li className="bssm-card">
      <div className="bssm-card-top">
        <OpenProductButton
          sku={parentSku}
          name={parentName}
          className="bssm-parent-thumb-btn"
          ariaLabel={`Product details for ${parentSku}`}
          onOpen={openProductInfo}
        >
          <div className="bssm-parent-thumb">
            <ProductImage sku={parentSku} alt={parentSku} />
          </div>
        </OpenProductButton>
        <div className="bssm-card-main">
          <div className="bssm-card-header">
            <OpenProductButton
              sku={parentSku}
              name={parentName}
              className="bssm-parent-heading"
              onOpen={openProductInfo}
            >
              <span className="bssm-parent-sku">{renderSkuWithSoftBreaks(parentSku)}</span>
            </OpenProductButton>
            <div className="bssm-card-header-actions">
              {onRemove ? (
                <button
                  type="button"
                  className="bssm-remove-btn"
                  onClick={onRemove}
                  disabled={removeDisabled}
                >
                  {removeLabel}
                </button>
              ) : null}
              <button
                type="button"
                className={`bssm-expand-btn${expanded ? ' bssm-expand-btn--open' : ''}`}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? `Hide components for ${parentSku}`
                    : `Show components for ${parentSku}`
                }
                onClick={() => setExpanded((prev) => !prev)}
              >
                <span className="bssm-expand-chevron" aria-hidden="true">▶</span>
              </button>
            </div>
          </div>
          {children}
        </div>
      </div>
      {expanded ? (
        components.length === 0 ? (
          <div className="bssm-components-empty">{emptyComponentsLabel}</div>
        ) : (
          <table className="bssm-component-table">
            <thead>
              <tr>
                <th scope="col" className="bssm-component-table-image">Image</th>
                <th scope="col">SKU</th>
                <th scope="col" className="bssm-component-table-qty">Qty</th>
                {onRemoveComponent ? <th scope="col" className="bssm-component-table-actions"> </th> : null}
              </tr>
            </thead>
            <tbody>
              {components.map((component) => (
                <tr key={component.sku}>
                  <td className="bssm-component-table-image">
                    <OpenProductButton
                      sku={component.sku}
                      name={component.name}
                      className="bssm-sku-open-btn"
                      ariaLabel={`Product details for ${component.sku}`}
                      onOpen={openProductInfo}
                    >
                      <div className="bssm-component-thumb">
                        <ProductImage sku={component.sku} alt={component.sku} />
                      </div>
                    </OpenProductButton>
                  </td>
                  <td className="bssm-component-table-sku">
                    <OpenProductButton
                      sku={component.sku}
                      name={component.name}
                      className="bssm-sku-open-btn bssm-sku-open-btn--text"
                      ariaLabel={`Product details for ${component.sku}`}
                      onOpen={openProductInfo}
                    >
                      {renderSkuWithSoftBreaks(component.sku)}
                    </OpenProductButton>
                  </td>
                  <td className="bssm-component-table-qty">
                    {onQuantityChange ? (
                      <input
                        type="number"
                        min={1}
                        className="bfm-qty-input"
                        aria-label={`Quantity for ${component.sku}`}
                        defaultValue={component.quantity}
                        disabled={componentRemoveDisabled}
                        onBlur={(event) => {
                          const next = Number.parseInt(event.target.value, 10);
                          if (!Number.isFinite(next) || next < 1) {
                            event.target.value = String(component.quantity);
                            return;
                          }
                          if (next !== component.quantity) {
                            onQuantityChange(component.sku, next);
                          }
                        }}
                      />
                    ) : (
                      component.quantity
                    )}
                  </td>
                  {onRemoveComponent ? (
                    <td className="bssm-component-table-actions">
                      <button
                        type="button"
                        className="bssm-remove-btn"
                        disabled={componentRemoveDisabled}
                        onClick={() => onRemoveComponent(component.sku)}
                      >
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
      {expanded && expandedFooter ? <div className="bssm-card-footer">{expandedFooter}</div> : null}
      {productInfoItem ? (
        <ProductInfoModal
          item={productInfoItem}
          onClose={() => setProductInfoItem(null)}
        />
      ) : null}
    </li>
  );
}

export default BundleAllowlistCard;
